import { useState } from 'react';
import type { Categoria } from '@/hooks/useStore';

interface Props {
  categorias: Categoria[];
  onAdd: (nome: string) => void;
  onUpdate: (id: string, nome: string) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}

export default function ModalCategorias({ categorias, onAdd, onUpdate, onDelete, onClose }: Props) {
  const [novaCategoria, setNovaCategoria] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingNome, setEditingNome] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  function handleAdd() {
    const nome = novaCategoria.trim();
    if (!nome) return;
    const jaExiste = categorias.some(c => c.nome.toLowerCase() === nome.toLowerCase());
    if (jaExiste) return;
    onAdd(nome);
    setNovaCategoria('');
  }

  function startEdit(cat: Categoria) {
    setEditingId(cat.id);
    setEditingNome(cat.nome);
  }

  function saveEdit() {
    if (!editingId || !editingNome.trim()) return;
    onUpdate(editingId, editingNome.trim());
    setEditingId(null);
    setEditingNome('');
  }

  function cancelEdit() {
    setEditingId(null);
    setEditingNome('');
  }

  return (
    <div className="fixed inset-0 z-50 modal-overlay flex items-center justify-center p-4">
      <div className="bg-dark-card border border-neon/15 rounded-2xl w-full max-w-sm p-5 animate-fade-in-up max-h-[85vh] flex flex-col shadow-[0_0_40px_rgba(57,255,20,0.07)]">
        {/* Header */}
        <div className="flex items-center justify-between mb-4 shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-neon/10">
              <i className="ri-price-tag-3-line text-neon text-sm"></i>
            </div>
            <h2 className="text-base font-bold text-dark-text">Categorias</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-dark-card2 text-dark-muted cursor-pointer"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Adicionar nova */}
        <div className="flex gap-2 mb-4 shrink-0">
          <input
            className="input-dark flex-1 text-sm"
            placeholder="Nova categoria..."
            value={novaCategoria}
            onChange={e => setNovaCategoria(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <button
            onClick={handleAdd}
            disabled={!novaCategoria.trim()}
            className="btn-gold px-3 py-2 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <i className="ri-add-line text-sm"></i>
          </button>
        </div>

        {/* Lista de categorias */}
        <div className="flex-1 overflow-y-auto space-y-2 min-h-0">
          {categorias.length === 0 && (
            <div className="text-center py-8">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-dark-card2 mx-auto mb-3">
                <i className="ri-price-tag-3-line text-dark-muted text-lg"></i>
              </div>
              <p className="text-sm text-dark-muted">Nenhuma categoria ainda</p>
              <p className="text-xs text-dark-muted/60 mt-1">Adicione sua primeira categoria acima</p>
            </div>
          )}

          {categorias.map(cat => (
            <div key={cat.id} className="bg-dark-card2 border border-dark-border rounded-xl px-3 py-2.5">
              {editingId === cat.id ? (
                <div className="flex gap-2 items-center">
                  <input
                    className="input-dark flex-1 text-sm py-1.5"
                    value={editingNome}
                    onChange={e => setEditingNome(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    autoFocus
                  />
                  <button
                    onClick={saveEdit}
                    className="w-7 h-7 flex items-center justify-center rounded-lg bg-neon/10 text-neon hover:bg-neon/20 cursor-pointer"
                  >
                    <i className="ri-check-line text-sm"></i>
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-dark-card text-dark-muted cursor-pointer"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              ) : confirmDeleteId === cat.id ? (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-red-400 flex-1">Excluir &quot;{cat.nome}&quot;?</p>
                  <button
                    onClick={() => { onDelete(cat.id); setConfirmDeleteId(null); }}
                    className="px-3 py-1 rounded-lg bg-red-500 text-white text-xs font-bold cursor-pointer whitespace-nowrap"
                  >
                    Sim
                  </button>
                  <button
                    onClick={() => setConfirmDeleteId(null)}
                    className="px-3 py-1 rounded-lg bg-dark-card border border-dark-border text-dark-muted text-xs cursor-pointer whitespace-nowrap"
                  >
                    Não
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-neon/60 shrink-0"></span>
                    <span className="text-sm text-dark-text font-medium">{cat.nome}</span>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => startEdit(cat)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-dark-card text-dark-muted hover:text-dark-text cursor-pointer transition-colors"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(cat.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-500/10 text-dark-muted hover:text-red-400 cursor-pointer transition-colors"
                    >
                      <i className="ri-delete-bin-line text-sm"></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={onClose}
          className="mt-4 w-full py-2.5 rounded-xl border border-dark-border text-dark-muted text-sm font-semibold cursor-pointer whitespace-nowrap shrink-0"
        >
          Fechar
        </button>
      </div>
    </div>
  );
}
